import { useDrag, useDrop } from "react-dnd";
import { Box, Stack, Typography, Chip, IconButton, Button } from "@mui/material";
import { MoreHoriz, Add } from "@mui/icons-material";
import TaskCard from "./TaskCard";

// ========================
// ColumnHeader Component
// ========================
function ColumnHeader({
  column,
  index,
  onRemoveColumn,
  onMoveColumn,
  totalColumns,
  taskCount,
  isReadOnly = false,
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "COLUMN",
    canDrag: () => !isReadOnly,
    item: { id: column.id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [column.id, index, isReadOnly]);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "COLUMN",
    drop: (item) => {
      if (item.index !== index && !isReadOnly) {
        onMoveColumn(item.index, index);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [index, isReadOnly]);

  return (
    <Box
      ref={(node) => {
        drag(drop(node));
      }}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isReadOnly ? "default" : "move",
        padding: "8px 12px",
        borderRadius: "4px",
        backgroundColor: isOver ? "rgba(108,92,231,0.1)" : "transparent",
        transition: "background-color 0.2s ease",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontWeight={500} fontSize={14} color="#1a1a1a">
            {column.label}
          </Typography>
          <Chip
            label={taskCount}
            size="small"
            sx={{
              height: 20,
              fontSize: "11px",
              bgcolor: "#ECECF0",
              color: "#717182",
              borderRadius: "10px",
              "& .MuiChip-label": { px: "8px" },
            }}
          />
        </Stack>
        {totalColumns > 1 && !isReadOnly && (
          <IconButton
            size="small"
            sx={{ color: "#717182" }}
            onClick={() => onRemoveColumn(index)}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

// ========================
// KanbanColumn Component
// ========================
export default function KanbanColumn({
  status,
  label,
  tasks = [],
  onDrop,
  onTaskClick,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onToggleComplete,
  column,
  columnIndex,
  onRemoveColumn,
  onMoveColumn,
  totalColumns,
  isReadOnly = false,
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "TASK",
    drop: (item) => {
      if (item.status !== status && !isReadOnly) {
        onDrop(item.id, status);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [status, isReadOnly]);

  const safeTasks = Array.isArray(tasks) ? tasks : [];

  return (
    <Box flexShrink={0} width={312}>
      <ColumnHeader
        column={column}
        index={columnIndex}
        onRemoveColumn={onRemoveColumn}
        onMoveColumn={onMoveColumn}
        totalColumns={totalColumns}
        taskCount={safeTasks.length}
        isReadOnly={isReadOnly}
      />
      <Box
        ref={drop}
        minHeight={400}
        sx={{
          bgcolor: isOver ? "rgba(108,92,231,0.05)" : "transparent",
          borderRadius: 2,
          border: isOver
            ? "1px dashed rgba(108,92,231,0.4)"
            : "1px solid transparent",
          transition: "all 0.15s ease",
          p: isOver ? 1 : 0,
        }}
      >
        {safeTasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onTaskClick={onTaskClick}
            onEditTask={onEditTask}
            onDeleteTask={onDeleteTask}
            onToggleComplete={onToggleComplete}
            isDragDisabled={isReadOnly} // Viewer cannot drag or edit
          />
        ))}
        
        {!isReadOnly && (
          <Button
            startIcon={<Add />}
            size="small"
            onClick={onAddTask}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              color: "#717182",
              textTransform: "none",
              fontWeight: 400,
              fontSize: 13,
              borderRadius: "8px",
              mt: 0.5,
              "&:hover": { color: "#1a1a1a", bgcolor: "#F3F3F5" },
            }}
          >
            Add Task
          </Button>
        )}
      </Box>
    </Box>
  );
}
